/**
 * 広告画像 Storage マイグレーション (Phase 34.3, owner only)
 *
 * 旧パス `marketing/ad-imgs/{adImageId}/{filename}`
 *   → 新フラットパス `marketing/ad-imgs/{adImageId}-{filename}`
 *
 * 1. Dry Run: 件数の集計のみ
 * 2. 実行: ファイルコピー + Firestore URL 書換（旧ファイルは残す）
 * 3. クリーンアップ: 旧ファイルを削除
 */
import { Loader2 } from 'lucide-react';
import { useState } from 'react';

import { Button } from '@/components/ui/button';
import { toast } from '@/components/ui/toast';
import { useMigrateAdImageStorage } from '@/hooks/useAdImages';

type Result = {
  scannedDocs: number;
  scannedUrls: number;
  migrated: number;
  alreadyMigrated: number;
  skipped: number;
  deleted: number;
  errors: { adImageId: string; reason: string }[];
};

export function AdImageMigrationTab() {
  const migrate = useMigrateAdImageStorage();
  const [lastResult, setLastResult] = useState<Result | null>(null);

  const run = async (args: { dryRun: boolean; deleteOld: boolean }) => {
    try {
      const res = await migrate.mutateAsync(args);
      setLastResult(res as Result);
      toast.success(
        `${args.dryRun ? 'Dry run' : args.deleteOld ? 'クリーンアップ' : '移行'}完了: scanned ${res.scannedDocs} docs / migrated ${res.migrated} / deleted ${res.deleted}`,
      );
    } catch (e) {
      toast.error(`失敗: ${e instanceof Error ? e.message : String(e)}`);
    }
  };

  return (
    <div className="space-y-6 pt-4 max-w-2xl">
      <section>
        <h3 className="text-sm font-semibold mb-2">
          Storage パスフラット化マイグレーション
        </h3>
        <p className="text-xs text-[var(--text-muted)] mb-4">
          旧パス <code>marketing/ad-imgs/{`{adImageId}`}/...</code> を{' '}
          <code>marketing/ad-imgs/{`{adImageId}`}-...</code> へ移行します。<br />
          Firestore の <code>slides[].background.url</code> /{' '}
          <code>slides[].intermediateImages.stepA/B</code> も自動で書き換えます。
        </p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
          <Button
            variant="secondary"
            onClick={() => run({ dryRun: true, deleteOld: false })}
            disabled={migrate.isPending}
          >
            {migrate.isPending && <Loader2 className="size-4 mr-1 animate-spin" />}
            1) Dry run（件数集計）
          </Button>
          <Button
            variant="primary"
            onClick={() => run({ dryRun: false, deleteOld: false })}
            disabled={migrate.isPending}
          >
            {migrate.isPending && <Loader2 className="size-4 mr-1 animate-spin" />}
            2) 実行（コピー + Firestore 更新）
          </Button>
          <Button
            variant="danger"
            onClick={() => {
              if (!confirm('旧ファイルを Storage から削除します。実行前に動作確認は完了していますか？')) return;
              run({ dryRun: false, deleteOld: true });
            }}
            disabled={migrate.isPending}
          >
            {migrate.isPending && <Loader2 className="size-4 mr-1 animate-spin" />}
            3) 旧ファイル削除
          </Button>
        </div>
      </section>

      {lastResult && (
        <section className="border border-[var(--border)] rounded p-3 text-xs">
          <h4 className="font-semibold text-sm mb-2">直近の結果</h4>
          <dl className="grid grid-cols-2 gap-x-4 gap-y-1">
            <dt className="text-[var(--text-muted)]">走査ドキュメント数</dt>
            <dd>{lastResult.scannedDocs}</dd>
            <dt className="text-[var(--text-muted)]">走査 URL 数</dt>
            <dd>{lastResult.scannedUrls}</dd>
            <dt className="text-[var(--text-muted)]">移行件数</dt>
            <dd>{lastResult.migrated}</dd>
            <dt className="text-[var(--text-muted)]">既にフラット</dt>
            <dd>{lastResult.alreadyMigrated}</dd>
            <dt className="text-[var(--text-muted)]">スキップ</dt>
            <dd>{lastResult.skipped}</dd>
            <dt className="text-[var(--text-muted)]">削除件数</dt>
            <dd>{lastResult.deleted}</dd>
          </dl>
          {lastResult.errors.length > 0 && (
            <div className="mt-3">
              <div className="text-[var(--accent-red)] font-semibold mb-1">
                エラー ({lastResult.errors.length})
              </div>
              <ul className="space-y-0.5 max-h-40 overflow-y-auto">
                {lastResult.errors.slice(0, 30).map((er, i) => (
                  <li key={i} className="font-mono text-[10px]">
                    [{er.adImageId}] {er.reason}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </section>
      )}
    </div>
  );
}
